import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { format } from 'date-fns';
import { Loader2, CalendarIcon, Wand2 } from 'lucide-react';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { apiFetch, ApiError } from '@/lib/api';
import { cn } from '@/lib/utils';
import { generateSerial } from '@/lib/serial';
import type { SerialKind } from '@/lib/serial';
import { useDateLocale } from '@/lib/date-locale';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
  SheetFooter,
} from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import EntitlementsCombobox from '@/components/app/entitlements-combobox';
import { ENTITLEMENT_OPTIONS } from '@/lib/entitlements';

const formSchema = z.object({
  serial: z.string().min(1, 'errors.validation.serialRequired'),
  type: z.enum(['perpetual', 'subscription', 'trial'] as const, {
    required_error: 'errors.validation.typeRequired',
  }),
  maxActivations: z.number().int().min(1).max(1000).default(1),
  unlimitedActivations: z.boolean().default(false),
  expiresAt: z.date().optional(),
  neverExpires: z.boolean().default(true),
  entitlements: z.array(z.object({ module: z.string(), tier: z.string() })).default([]),
});

type FormValues = z.infer<typeof formSchema>;

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function CreateLicenseSheet({ open, onOpenChange }: Props) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const dateLocale = useDateLocale();
  const [networkError, setNetworkError] = React.useState<string | null>(null);
  const [generatingSerial, setGeneratingSerial] = React.useState(false);
  // The serial format the auto-generate button mints (#163): a robot serial or a
  // software key. The server owns both formats (`POST /licenses/serial/generate`
  // takes `{ kind }`); the selector only decides which one is asked for.
  const [serialKind, setSerialKind] = React.useState<SerialKind>('robot');
  const {
    register,
    handleSubmit,
    control,
    watch,
    setValue,
    setError,
    reset,
    formState: { errors, isSubmitting, isValid },
  } = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    mode: 'onChange',
    defaultValues: {
      serial: '',
      type: 'perpetual' as const,
      maxActivations: 1,
      unlimitedActivations: false,
      neverExpires: true,
      entitlements: [] as { module: string; tier: string }[],
    },
  });
  const unlimited = watch('unlimitedActivations');
  const neverExpires = watch('neverExpires');

  React.useEffect(() => {
    if (open) {
      reset();
      setNetworkError(null);
      setSerialKind('robot');
    }
  }, [open, reset]);

  const handleGenerateSerial = async () => {
    setGeneratingSerial(true);
    try {
      const serial = await generateSerial(serialKind);
      setValue('serial', serial, { shouldValidate: true });
    } catch {
      toast.error(t('licenses.new.generateError'));
    } finally {
      setGeneratingSerial(false);
    }
  };

  const onSubmit = async (data: FormValues) => {
    setNetworkError(null);
    const payload: Record<string, unknown> = {
      serial: data.serial,
      type: data.type,
      entitlements: data.entitlements,
    };
    payload.maxActivations = data.unlimitedActivations ? 1000 : data.maxActivations;
    if (!data.neverExpires && data.expiresAt) payload.expiresAt = data.expiresAt.toISOString();
    try {
      const resp = await apiFetch('/licenses', { method: 'POST', body: JSON.stringify(payload) });
      const result = (await resp.json()) as { id: string };
      toast.success(t('licenses.new.toastCreated', { serial: data.serial }));
      onOpenChange(false);
      navigate(`/licenses/${result.id}`);
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) {
        setError('root', { message: t('errors.accessDenied') });
      } else if (err instanceof ApiError && err.status >= 400 && err.status < 500) {
        try {
          const body = JSON.parse(err.message);
          if (body.errors) {
            for (const [k, v] of Object.entries(body.errors))
              setError(k as keyof FormValues, { message: Array.isArray(v) ? v[0] : String(v) });
          } else if (body.error) setError('root', { message: body.error });
          else setError('root', { message: err.message });
        } catch {
          setError('root', { message: err.message });
        }
      } else {
        setNetworkError(t('licenses.new.networkError'));
      }
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-lg">
        <SheetHeader>
          <SheetTitle>{t('licenses.new.title')}</SheetTitle>
          <SheetDescription>{t('licenses.new.description')}</SheetDescription>
        </SheetHeader>
        <form id="create-license-form" onSubmit={handleSubmit(onSubmit)} className="py-2">
          <fieldset className="space-y-4 border-b pb-6">
            <legend className="mb-4 text-sm font-semibold text-foreground">
              {t('licenses.new.groupIdentity')}
            </legend>
            <div className="space-y-2">
              <Label htmlFor="serialKind">{t('licenses.new.kind')}</Label>
              <Select value={serialKind} onValueChange={(v) => setSerialKind(v as SerialKind)}>
                <SelectTrigger id="serialKind" data-testid="serial-kind">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="robot">{t('licenses.kind.robot')}</SelectItem>
                  <SelectItem value="software">{t('licenses.kind.software')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="serial">{t('licenses.new.serial')}</Label>
              <div className="flex gap-2">
                <Input
                  id="serial"
                  {...register('serial')}
                  className="flex-1 font-mono"
                  autoComplete="off"
                />
                <Button
                  type="button"
                  variant="outline"
                  disabled={generatingSerial}
                  onClick={handleGenerateSerial}
                >
                  <Wand2 className="mr-2 h-4 w-4" />
                  {t('licenses.new.autoGenerate')}
                </Button>
              </div>
              {errors.serial && (
                <p className="text-sm text-destructive" role="alert">
                  {t(String(errors.serial.message))}
                </p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="type">{t('licenses.new.type')}</Label>
              <Controller
                name="type"
                control={control}
                render={({ field }) => (
                  <Select onValueChange={field.onChange} value={field.value}>
                    <SelectTrigger id="type">
                      <SelectValue placeholder={t('licenses.new.selectType')} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="perpetual">{t('common.type.perpetual')}</SelectItem>
                      <SelectItem value="subscription">{t('common.type.subscription')}</SelectItem>
                      <SelectItem value="trial">{t('common.type.trial')}</SelectItem>
                    </SelectContent>
                  </Select>
                )}
              />
              {errors.type && (
                <p className="text-sm text-destructive" role="alert">
                  {t(String(errors.type.message))}
                </p>
              )}
            </div>
          </fieldset>

          <fieldset className="space-y-4 border-b py-6">
            <legend className="mb-4 text-sm font-semibold text-foreground">
              {t('licenses.new.groupLimits')}
            </legend>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="maxActivations">{t('licenses.new.maxActivations')}</Label>
                <div className="flex items-center gap-2">
                  <Switch
                    id="unlimitedActivations"
                    checked={unlimited}
                    onCheckedChange={(v) => setValue('unlimitedActivations', v)}
                  />
                  <Label htmlFor="unlimitedActivations" className="cursor-pointer">
                    {t('licenses.new.unlimited')}
                  </Label>
                </div>
              </div>
              <Input
                id="maxActivations"
                type="number"
                min={1}
                max={1000}
                disabled={unlimited}
                className="tabular-nums"
                {...register('maxActivations', { valueAsNumber: true })}
              />
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>{t('licenses.new.expiresAt')}</Label>
                <div className="flex items-center gap-2">
                  <Switch
                    id="neverExpires"
                    checked={neverExpires}
                    onCheckedChange={(v) => setValue('neverExpires', v)}
                  />
                  <Label htmlFor="neverExpires" className="cursor-pointer">
                    {t('licenses.new.neverExpires')}
                  </Label>
                </div>
              </div>
              <Controller
                name="expiresAt"
                control={control}
                render={({ field }) => (
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className={cn(
                          'w-full justify-start text-left font-normal',
                          !field.value && 'text-muted-foreground'
                        )}
                        disabled={neverExpires}
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {field.value
                          ? format(field.value, 'PPP', { locale: dateLocale })
                          : t('licenses.new.pickDate')}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0">
                      <Calendar
                        mode="single"
                        selected={field.value}
                        onSelect={field.onChange}
                        locale={dateLocale}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                )}
              />
            </div>
          </fieldset>

          <fieldset className="py-6">
            <legend className="mb-4 text-sm font-semibold text-foreground">
              {t('licenses.new.groupModules')}
            </legend>
            <div className="space-y-2">
              <Label htmlFor="entitlements">{t('licenses.new.entitlements')}</Label>
              <Controller
                name="entitlements"
                control={control}
                render={({ field }) => (
                  <EntitlementsCombobox
                    options={ENTITLEMENT_OPTIONS}
                    selected={field.value}
                    onChange={field.onChange}
                    disabled={isSubmitting}
                  />
                )}
              />
            </div>
          </fieldset>
          {(errors.root || networkError) && (
            <p className="text-sm text-destructive" role="alert" aria-live="polite">
              {errors.root?.message ?? networkError}
            </p>
          )}
          <SheetFooter className="flex-col-reverse sm:flex-row sm:justify-end">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isSubmitting}
            >
              {t('common.cancel')}
            </Button>
            <Button type="submit" disabled={!isValid || isSubmitting}>
              {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {t('licenses.new.submit')}
            </Button>
          </SheetFooter>
        </form>
      </SheetContent>
    </Sheet>
  );
}

export default function NewLicensePage() {
  const navigate = useNavigate();
  React.useEffect(() => {
    navigate('/licenses?action=new', { replace: true });
  }, [navigate]);
  return null;
}
